import _get from 'lodash/get';
import _uniq from 'lodash/uniq';
import { defineMessages, IntlShape } from 'react-intl';
import * as abac from '@lifeomic/abac';

const {
  enforce: abacEnforce,
  enforceLenient: abacEnforceLenient,
  privilegesLenient: abacPrivilegesLenient,
} = abac;

const messages = defineMessages({
  allProjectsLabel: {
    id: 'abac.allProjects',
    defaultMessage: 'All projects',
  },
});

export function enforce(
  operation: string,
  policy: abac.AbacReducedPolicy,
  attributes?: Record<string, unknown>,
) {
  if (!policy) {
    return false;
  } else {
    return abacEnforceLenient(operation, policy, attributes);
  }
}

export function strictEnforce(
  operation: string,
  policy: abac.AbacReducedPolicy,
  attributes?: Record<string, unknown>,
) {
  if (!policy) {
    return false;
  } else {
    return abacEnforce(operation, policy, attributes);
  }
}

export function privileges(
  policy: abac.AbacReducedPolicy,
  attributes?: Record<string, unknown>,
) {
  if (!policy) {
    return [];
  } else {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return abacPrivilegesLenient(policy, attributes);
  }
}

export function hasAccessToProject(
  policy: abac.AbacReducedPolicy,
  project: any,
) {
  const attributes = { resource: { dataset: project.id } };
  return (
    enforce(permissions.readData, policy, attributes) ||
    enforce(permissions.readMaskedData, policy, attributes) ||
    enforce(permissions.downloadFile, policy, attributes)
  );
}

export function hasAccessToCohort(
  policy: abac.AbacReducedPolicy,
  project: any,
  cohort: any,
) {
  const attributes = {
    resource: { dataset: project?.id, cohorts: cohort?.id },
  };
  return (
    enforce(permissions.readData, policy, attributes) ||
    enforce(permissions.readMaskedData, policy, attributes)
  );
}

// Permissions that map to string values for abac service
export const permissions = {
  accessAdmin: 'accessAdmin',
  billingAdmin: 'billingAdmin',
  projectAdmin: 'projectAdmin',
  accountAdmin: 'accountAdmin',
  ehrAdmin: 'ehrAdmin',
  createData: 'createData',
  readData: 'readData',
  readMaskedData: 'readMaskedData',
  updateData: 'updateData',
  deleteData: 'deleteData',
  downloadFile: 'downloadFile',
  layoutAdmin: 'layoutAdmin',
  apiKeyUser: 'apiKeyUser',
  developApps: 'developApps',
  inviteUsers: 'inviteUsers',
  ruleAdmin: 'ruleAdmin',
  publishContent: 'publishContent',
  ingestFromEHR: 'ingestFromEHR',
  fileAdmin: 'fileAdmin',
  workflowAdmin: 'workflowAdmin',
  createGenomicData: 'createGenomicData',
  clinicalTrialAlertUser: 'clinicalTrialAlertUser',
  employeeAdmin: 'employeeAdmin',
};

export const resourceTypes = {
  patient: 'Patient',
  insights: 'insights',
  questionnaire: 'Questionnaire',
};

export const complexPermissions = {
  readData: {
    all: 'readData',
    some: 'readSomeData',
    none: 'readNoData',
  },
  readMaskedData: {
    all: 'readMaskedData',
    some: 'readSomeMaskedData',
    none: 'readNoMaskedData',
  },
  createData: {
    all: 'createData',
    some: 'createSomeData',
    none: 'createNoData',
  },
  updateData: {
    all: 'updateData',
    some: 'updateSomeData',
    none: 'updateNoData',
  },
  deleteData: {
    all: 'deleteData',
    some: 'deleteSomeData',
    none: 'deleteNoData',
  },
};

// Valid resource types for readData
export const dataPermissions = {
  insights: resourceTypes.insights,
  patient: resourceTypes.patient,
};

// valid resource types for readMaskedData
export const maskedDataPermissions = {
  insights: resourceTypes.patient,
};

export const createDataPermissions = {
  patient: resourceTypes.patient,
};
export const updateDataPermissions = {
  patient: resourceTypes.patient,
};
export const deleteDataPermissions = {
  patient: resourceTypes.patient,
};

// For transforming privilege form values to abac permissions
export const someToAllMap = Object.entries(complexPermissions).reduce(
  (acc, [, p]) => {
    return {
      ...acc,
      [p.some]: p.all,
    };
  },
  {},
);

// For transforming abac permissions to privilege form values
export const allToSomeMap = Object.entries(complexPermissions).reduce(
  (acc, [, p]) => {
    return {
      ...acc,
      [p.all]: p.some,
    };
  },
  {},
);

// Values for 'None' selection for use in privilege form.
export const nullPrivileges = Object.entries(complexPermissions).map(
  ([, p]) => p.none,
);

// Form values that should contain resource.type
export const privilegesWithResourceTypes = Object.entries(
  complexPermissions,
).map(([, p]) => p.some);

export const privilegeHasResourceTypes = (privilege: string) => {
  return privilegesWithResourceTypes.includes(privilege);
};

export const privilegeIsResourceType = (privilege: string) => {
  const privWithChildren = privilegesMap.filter(p => p.children !== undefined);
  const childPrivKeys = privWithChildren.flatMap((p: any) =>
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    p.children.map(c => c.key),
  );
  return childPrivKeys.includes(privilege);
};

export const isNullPrivilege = (privilege: string) => {
  return nullPrivileges.includes(privilege);
};

export const privilegeIsCustomResource = (privilege: string) => {
  return privilege.indexOf('.custom.') > -1;
};

export type Privilege = {
  key: string;
  label: string;
  description: string;
  allowCustomResources?: boolean;
  isCustomResource?: boolean;
  isChild?: boolean;
  children?: {
    key: string;
    value: string;
    label: string;
    description: string;
  }[];
};

export const privilegesMap: Privilege[] = [
  {
    key: permissions.readData,
    label: 'View Data',
    description: 'allows a user to list files and view subject data',
    allowCustomResources: true,
    children: [
      {
        key: `${permissions.readData}.${dataPermissions.insights}`,
        value: dataPermissions.insights,
        label: 'Insights',
        description: 'allows a user to view insights data',
      },
      {
        key: `${permissions.readData}.${dataPermissions.patient}`,
        value: dataPermissions.patient,
        label: 'Patient',
        description: 'allows a user to view patient data',
      },
    ],
  },
  {
    key: permissions.readMaskedData,
    label: 'View Masked Data',
    description:
      'allows a user to list files and view subject data with identifying information masked',
    allowCustomResources: true,
    children: [
      {
        key: `${permissions.readData}.${maskedDataPermissions.insights}`,
        value: maskedDataPermissions.insights,
        label: 'Insights',
        description: 'allow a user to view masked insights data',
      },
    ],
  },
  {
    key: permissions.downloadFile,
    label: 'Download File',
    description: 'allows a user to download file based data',
  },
  {
    key: permissions.createData,
    label: 'Create Data',
    description: 'allows a user to add new subject data and files',
    allowCustomResources: true,
    children: [
      {
        key: `${permissions.createData}.${createDataPermissions.patient}`,
        value: createDataPermissions.patient,
        label: 'Patient',
        description: 'allows a user to create patient data',
      },
    ],
  },
  {
    key: permissions.deleteData,
    label: 'Delete Data',
    description: 'allows a user to delete subject data and files',
    allowCustomResources: true,
    children: [
      {
        key: `${permissions.deleteData}.${deleteDataPermissions.patient}`,
        value: deleteDataPermissions.patient,
        label: 'Patient',
        description: 'allows a user to delete patient data',
      },
    ],
  },
  {
    key: permissions.updateData,
    label: 'Update Data',
    description: 'allows a user to alter existing subject data and files',
    allowCustomResources: true,
    children: [
      {
        key: `${permissions.updateData}.${updateDataPermissions.patient}`,
        value: updateDataPermissions.patient,
        label: 'Patient',
        description: 'allows a user to udpate patient data',
      },
    ],
  },
  {
    key: permissions.inviteUsers,
    label: 'Invite Users',
    description: 'allows a user to invite other users to specific group(s)',
  },
  {
    key: permissions.ruleAdmin,
    label: 'Administer Rules',
    description: 'allows a user to create, update, and delete rules',
  },
  {
    key: permissions.layoutAdmin,
    label: 'Administer Layouts',
    description:
      'allows a user to create, update, and delete Insights and subject viewer layouts',
  },
  {
    key: permissions.publishContent,
    label: 'Publish Content',
    description: 'allows a user to publish content to marketplace',
  },
  {
    key: permissions.accessAdmin,
    label: 'Administer Access',
    description:
      'allows a user to create, update, and delete groups and policies',
  },
  {
    key: permissions.accountAdmin,
    label: 'Administer Account',
    description: 'allows a user to update and delete the account',
  },
  {
    key: permissions.ehrAdmin,
    label: 'EHR',
    description: 'allows a user to create, update, and delete EHRs',
  },
  {
    key: permissions.billingAdmin,
    label: 'Administer Billing',
    description: 'allows a user to manage billing and usage',
  },
  {
    key: permissions.projectAdmin,
    label: 'Administer Projects',
    description: 'allows a user to create, update, and delete projects',
  },
  {
    key: permissions.apiKeyUser,
    label: 'Manage API Keys',
    description:
      'allows a user to create and revoke their API keys for account access',
  },
  {
    key: permissions.developApps,
    label: 'Develop Apps',
    description: 'allows a user to develop and release custom built apps',
  },
  {
    key: permissions.ingestFromEHR,
    label: 'Ingest From EHR',
    description: 'allows a user to trigger EHR ingestions',
  },
  {
    key: permissions.fileAdmin,
    label: 'View Files',
    description: 'allows a user to view file list and administer file actions',
  },
  {
    key: permissions.workflowAdmin,
    label: 'Run Workflows',
    description:
      'allows a user to run workflows and setup workflow based file actions',
  },
  {
    key: permissions.employeeAdmin,
    label: 'Manage Employees',
    description: 'allows a user to view and manage employees',
  },
  {
    key: permissions.createGenomicData,
    label: 'Manage Genomic Ingestions',
    description:
      'allows a user to view and administer genomic ingestions and genomic ingestion file actions',
  },
  {
    key: permissions.clinicalTrialAlertUser,
    label: 'Manage Clinical Trial Alerts',
    description:
      'allows a user to watch clinical trial filters and favorite clinical trials',
  },
];

export const labelFromKey = (intl: IntlShape, key: string) => {
  const entry = privilegesMap.find(privilege => privilege.key === key);
  if (entry) {
    return intl.formatMessage({
      id: `abac.privileges.${entry.key}.label`,
      defaultMessage: entry.label,
    });
  } else {
    return key;
  }
};

/**
 * Iterates through the children of the parent privilege to find labels
 *
 * @param {Object} intl i18n Object
 * @param {string[]} keys Strings of resource keys selected
 * @param {string} parent Parent privilege that contains children mapping
 *
 * @returns {array} Array containing the label strings
 */
export const resourceLabelsFromKeys = (
  intl: IntlShape,
  keys: string[],
  parent: string,
) => {
  const entry = privilegesMap.find(privilege => privilege.key === parent);
  if (entry) {
    const children = entry.children;
    // keys comes in as a nested array when more than one resource is present
    if (Array.isArray(keys[0])) keys = keys[0];
    return keys
      .map(key => {
        // The two different summary views send the data slightly different
        const childEntry = children?.find(
          child => child.value === key || child.key === key,
        );
        if (childEntry) {
          return intl.formatMessage({
            id: `abac.privileges.${entry.key}.${childEntry.key}.label`,
            defaultMessage: childEntry.label,
          });
        } else if (!privilegeIsCustomResource(key)) return key;
      })
      .filter(key => !!key);
  } else {
    return keys;
  }
};

/**
 * Enforces XOR logic on selecting data privileges vs masked data privileges
 * in the abac policy privilege selector
 *
 * @param {Privilege} item The current privilege being evaluated
 * @param {string[]} selectedPrivileges The other privileges selected in the form
 */
export const shouldDisablePrivilegeSelection = (
  item: Privilege,
  selectedPrivileges: string[] = [],
) => {
  const privilege = item.key;

  if (
    [
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      allToSomeMap[permissions.readMaskedData],
      permissions.readMaskedData,
    ].includes(privilege) &&
    (selectedPrivileges.includes(permissions.readData) ||
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      selectedPrivileges.includes(allToSomeMap[permissions.readData]))
  ) {
    return true;
  }

  if (
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    [allToSomeMap[permissions.readData], permissions.readData].includes(
      privilege,
    ) &&
    (selectedPrivileges.includes(permissions.readMaskedData) ||
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      selectedPrivileges.includes(allToSomeMap[permissions.readMaskedData]))
  ) {
    return true;
  }

  return false;
};

export function policyIsAccessAdmin(policy: abac.AbacReducedPolicy) {
  return Boolean(policy?.rules?.[permissions.accessAdmin] === true);
}

export function policyAllowsUserInvite(policy: abac.AbacReducedPolicy) {
  return Boolean(
    policy?.rules?.[permissions.accessAdmin] ||
      policy?.rules?.[permissions.inviteUsers],
  );
}

/**
 * Determines if the user has full access to the Account Management page or not
 * based on their evaluated policy. A few permissions that do not apply to
 * the account management pages are filtered out intentionally.
 *
 * @param {Object} policy - The evaluated policy
 * @returns {Boolean} - true if policy has all expected permissions, otherwise false
 */
export function hasFullAccountManagementPrivileges(
  policy: abac.AbacReducedPolicy,
): boolean {
  const permissionKeys = Object.values(permissions).filter(
    p =>
      p !== permissions.developApps &&
      p !== permissions.readMaskedData &&
      p !== permissions.layoutAdmin &&
      p !== permissions.apiKeyUser &&
      p !== permissions.ehrAdmin &&
      p !== permissions.publishContent &&
      p !== permissions.ruleAdmin &&
      p !== permissions.ingestFromEHR &&
      p != permissions.workflowAdmin &&
      p != permissions.fileAdmin &&
      p !== permissions.createGenomicData &&
      p !== permissions.clinicalTrialAlertUser,
  );
  return permissionKeys.every(key => enforce(key, policy));
}

/**
 * Creates an array of projects and their associated policies.
 *
 * @param {Object} policy - The evaluated policy
 * @param {Array} projects - All available projects to do a lookup from project ID -> project name
 * @param {Object} intl - The `intl` reference for translation lookups
 */
export function mapPoliciesToProjects(
  policy: abac.AbacReducedPolicy,
  projects: Array<any>,
  intl: IntlShape,
) {
  const { rules } = policy;

  if (!rules) return [];

  const keys = Object.keys(rules);
  if (keys.length === 0) return [];

  const allProjectsLabel = messages.allProjectsLabel.defaultMessage;

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const projectToPolicies = keys.reduce((acc, key) => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    if (!permissions[key]) {
      return acc;
    }

    const datasets = rules[key];

    // `datasets` can take two shapes, either a boolean (not per-project based)
    // or an array (applies to one or many projects)
    if (datasets === true) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const allProjects = acc.find(pr => pr.projectName === allProjectsLabel);
      if (allProjects) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        allProjects.policies.push(key);
      } else {
        acc.push(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          { projectName: allProjectsLabel, policies: [key] },
        );
      }

      return acc;
    }

    const projectIds = (datasets as Record<string, any>[])
      .map(d => d['resource.dataset']?.value)
      .filter(Boolean); // filter out any falsy vals

    const projectAndPolicies = _uniq(projectIds).reduce((rows, p) => {
      const projectName = _get(
        projects.find(pr => pr.id === p),
        'name',
        '',
      );
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const existingProject = acc.find(pr => pr.projectName === projectName);

      if (existingProject) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        existingProject.policies.push(key);
      } else {
        rows.push({
          projectName,
          policies: [key],
        });
      }
      return rows;
    }, []);

    return projectAndPolicies ? [...acc, ...projectAndPolicies] : [...acc];
  }, []);

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return projectToPolicies.map(p => {
    return {
      projectName: p.projectName,
      policies: p.policies.map((k: any) => labelFromKey(intl, k)).join(', '),
    };
  });
}
